import { __decorate } from "tslib";
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import HelixElement from '../../shared/types/helix-element.js';
import { watch } from '../../shared/directives/watch.decorator.js';
import { arrow, autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { localized, msg } from '@lit/localize';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { toggletipStyles } from './toggletip.styles.js';
import '../icon/icon.js';
let HlxToggletip = class HlxToggletip extends HelixElement {
    constructor() {
        super(...arguments);
        this.ariaHasPopup = 'true';
        this.title = msg('more info');
        this.open = false;
        this.inverted = false;
        this.state = 'hidden';
        this.placement = 'top';
        this.animationDuration = 250;
        this.hideTimeout = null;
        this.showTimeout = null;
        this.hide = () => {
            if (this.state === 'hidden' || this.state === 'fadeout') {
                return undefined;
            }
            if (this.showTimeout) {
                window.clearTimeout(this.showTimeout);
            }
            this.open = false;
            this.state = 'fadeout';
            this.hideTimeout = window.setTimeout(() => {
                this.state = 'hidden';
            }, this.animationDuration);
        };
        this.handleClick = (e) => {
            // in Chrome, if the element is in a label, the default behavior for
            // Space/Enter hit there is start typing  in a form field, so that
            // need to be prevented
            e.preventDefault();
            if (this.state === 'shown' || this.state === 'fadein') {
                this.hide();
            }
            else {
                this.show();
            }
        };
        this.handleFocusout = (e) => {
            this.hide();
        };
        this.handleKeyDown = (event) => {
            if (this.open && event.key === 'Escape') {
                event.stopPropagation();
                this.hide();
            }
        };
    }
    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.cleanupToggletip = autoUpdate(this.targetEl, this.bodyEl, () => this.initToggletip());
    }
    async handlePlacementChange() {
        this.initToggletip();
    }
    connectedCallback() {
        super.connectedCallback();
        const style = getComputedStyle(this);
        const durationCss = style.getPropertyValue('--hlx-toggletip-animation-duration');
        if (durationCss) {
            let duration = parseFloat(durationCss);
            const durationUnit = durationCss.replace(String(duration), '');
            if (durationUnit === 's') {
                duration = duration * 1000;
            }
            this.animationDuration = duration;
        }
        this.addEventListener('focusout', this.handleFocusout);
        this.addEventListener('keydown', this.handleKeyDown);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupToggletip();
        this.removeEventListener('focusout', this.handleFocusout);
        this.removeEventListener('keydown', this.handleKeyDown);
    }
    attributeChangedCallback(name, _old, value) {
        super.attributeChangedCallback(name, _old, value);
        if (name === 'open') {
            if (value || value === '') {
                this.show();
            }
            else {
                this.hide();
            }
        }
    }
    render() {
        return html `
            <div
                part="hlx-toggletip-base"
                class=${classMap({
            'hlx-toggletip': true,
            'hlx-toggletip-inverted': this.inverted,
            'hlx-toggletip-open': this.state === 'shown',
            'hlx-toggletip-fadein': this.state === 'fadein',
            'hlx-toggletip-fadeout': this.state === 'fadeout',
            'hlx-toggletip-removed': this.state === 'hidden',
        })}>
                <!-- See https://exas.atlassian.net/browse/HLX-432
                     By default button doesn't get focus state on click in Safari
                     so handleKeyDown is not called at all, adding tabindex fixes that behavior -->
                <button
                    class="hlx-toggletip-trigger"
                    part="hlx-toggletip-trigger"
                    tabindex="0"
                    @click=${this.handleClick}>
                    <slot name="trigger">
                        <hlx-icon
                            class="default-trigger-icon"
                            size="14"
                            name="circle-info"
                            slot="icon"
                            title=${this.title}></hlx-icon>
                    </slot>
                </button>

                <div
                    part="hlx-toggletip-body"
                    id="toggletip"
                    class="hlx-toggletip-body hlx-animation-with-prefers-reduced-motion"
                    role="dialog"
                    title="${this.title}"
                    data-testid="toggletip-body"
                    data-placement=${this.placement}
                    aria-live=${this.open ? 'polite' : 'off'}>
                    <div class="hlx-toggletip-body-container">
                        <slot part="content" class="hlx-toggletip-content"></slot>
                        <button
                            class="hlx-toggletip-close-button"
                            kind="tertiary"
                            size="small"
                            @click=${this.hide}>
                            <hlx-icon
                                size="16"
                                name="xmark"
                                slot="icon"
                                title=${msg('Close icon')}></hlx-icon>
                        </button>
                    </div>
                    <div part="arrow" class="hlx-toggletip-arrow" role="presentation"></div>
                </div>
            </div>
        `;
    }
    show() {
        if (this.state === 'shown' || this.state === 'fadein') {
            return undefined;
        }
        if (this.hideTimeout) {
            window.clearTimeout(this.hideTimeout);
        }
        this.open = true;
        this.state = 'fadein';
        this.showTimeout = window.setTimeout(() => {
            this.state = 'shown';
        }, this.animationDuration);
    }
    initToggletip() {
        const side = this.placement.split('-')[0];
        computePosition(this.targetEl, this.bodyEl, {
            placement: this.placement,
            middleware: [
                offset(10),
                flip(),
                shift(),
                arrow({
                    element: this.arrowEl,
                    padding: side === 'right' || side === 'left' ? 16 : 28,
                }),
            ],
        }).then(({ x, y, middlewareData, placement }) => {
            Object.assign(this.bodyEl.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
            if (middlewareData.arrow) {
                const side = placement.split('-')[0];
                const staticSide = {
                    top: 'bottom',
                    right: 'left',
                    bottom: 'top',
                    left: 'right',
                }[side];
                const { x, y } = middlewareData.arrow;
                Object.assign(this.arrowEl.style, {
                    left: x != null ? `${x}px` : '',
                    top: y != null ? `${y}px` : '',
                    right: '',
                    bottom: '',
                    [staticSide]: `${-this.arrowEl.offsetWidth / 2}px`,
                });
            }
        });
    }
};
HlxToggletip.styles = [componentStyles, toggletipStyles];
__decorate([
    query('.hlx-toggletip-body')
], HlxToggletip.prototype, "bodyEl", void 0);
__decorate([
    query('.hlx-toggletip-trigger')
], HlxToggletip.prototype, "targetEl", void 0);
__decorate([
    query('.hlx-toggletip-arrow')
], HlxToggletip.prototype, "arrowEl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'aria-haspopup' })
], HlxToggletip.prototype, "ariaHasPopup", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxToggletip.prototype, "title", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxToggletip.prototype, "open", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxToggletip.prototype, "inverted", void 0);
__decorate([
    state()
], HlxToggletip.prototype, "state", void 0);
__decorate([
    property({ type: String })
], HlxToggletip.prototype, "placement", void 0);
__decorate([
    watch('placement', { waitUntilFirstUpdate: true })
], HlxToggletip.prototype, "handlePlacementChange", null);
HlxToggletip = __decorate([
    customElement('hlx-toggletip'),
    localized()
], HlxToggletip);
export { HlxToggletip };
//# sourceMappingURL=toggletip.js.map